import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon, PhotoIcon, CameraIcon } from '@heroicons/react/24/outline';
import { useCreateMomentMutation, useGetUploadUrlMutation } from '../../store/api/localApi';
import { useToast } from '../Toast';
import ObservationPhotoUpload, { UploadingPhoto } from '../observations/ObservationPhotoUpload';

interface Props {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  /** The live session this moment is being shared against. */
  sessionId: string;
}

const CAPTION_LIMIT = 280;
const MAX_PHOTOS = 4;

/**
 * "Share a moment" — the warm, parent-facing counterpart to the observation
 * modal, launched from the RunSession roster. Photo-first: at least one photo
 * is required, the caption is optional. Deliberately stripped of the
 * observation modal's learning-goal picker and visibility control — a moment
 * is always a photo shared with the child's parents, nothing to tag or gate.
 *
 * The photo upload flow (presign → PUT to S3 → collect {s3Key, mimeType,
 * sizeBytes}) mirrors ObservationCreateModal exactly, swapping only the
 * presign purpose to MOMENT_PHOTO (contextId is the sessionId).
 */
const MomentCreateModal: React.FC<Props> = ({
  open,
  onClose,
  studentId,
  studentName,
  sessionId,
}) => {
  const { showSuccess, showError } = useToast();
  const [createMoment, { isLoading: isSaving }] = useCreateMomentMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();

  const [caption, setCaption] = useState('');
  const [photos, setPhotos] = useState<UploadingPhoto[]>([]);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) {
      setCaption('');
      setPhotos([]);
      closeBtnRef.current?.focus();
    }
  }, [open, studentId, sessionId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const anyUploading = photos.some((p) => p.uploading);
  const readyPhotos = photos.filter((p) => p.s3Key && !p.error);
  // Photo-first: at least one successfully-uploaded photo is required, the
  // caption is optional.
  const canSubmit = readyPhotos.length > 0 && !anyUploading && !isSaving;

  if (!open) return null;

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const slots = MAX_PHOTOS - photos.length;
    const fresh = Array.from(files).slice(0, slots);

    for (const file of fresh) {
      const previewUrl = URL.createObjectURL(file);
      const next: UploadingPhoto = {
        s3Key: '',
        mimeType: file.type,
        sizeBytes: file.size,
        previewUrl,
        uploading: true,
      };
      setPhotos((p) => [...p, next]);
      try {
        const { uploadUrl, s3Key } = await getUploadUrl({
          purpose: 'MOMENT_PHOTO',
          contextId: sessionId,
          mimeType: file.type,
          sizeBytes: file.size,
          fileName: file.name,
        }).unwrap();
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!putResp.ok) throw new Error(`S3 ${putResp.status}`);
        setPhotos((p) =>
          p.map((q) => (q.previewUrl === previewUrl ? { ...q, s3Key, uploading: false } : q))
        );
      } catch {
        setPhotos((p) =>
          p.map((q) =>
            q.previewUrl === previewUrl ? { ...q, uploading: false, error: 'Failed' } : q
          )
        );
      }
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const result = await createMoment({
        sessionId,
        studentId,
        caption: caption.trim() || undefined,
        photos: readyPhotos.map(({ s3Key, mimeType, sizeBytes, width, height }) => ({
          s3Key,
          mimeType,
          sizeBytes,
          width,
          height,
        })),
      }).unwrap();
      const n = result.recipientCount;
      showSuccess(
        n > 0 ? `Shared with ${n} parent${n === 1 ? '' : 's'}.` : 'Moment shared.',
        'moment-created-toast'
      );
      onClose();
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to share moment.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="moment-create-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="moment-create-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          data-testid="moment-create-backdrop"
          aria-hidden="true"
        />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 id="moment-create-title" className="text-lg font-semibold text-gray-900">
              Share a moment — {studentName}
            </h3>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              data-testid="moment-create-cancel"
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500 rounded"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Photos — required, 1–4. Photo-first: this is the primary field. */}
          <div className="mb-4">
            <ObservationPhotoUpload
              photos={photos}
              onRemove={(i) => {
                URL.revokeObjectURL(photos[i].previewUrl);
                setPhotos((p) => p.filter((_, idx) => idx !== i));
              }}
            />
            {photos.length < MAX_PHOTOS && (
              <div className="flex items-center gap-3 mt-2">
                {/* Take photo: launches the rear camera on mobile (`capture`);
                    on desktop it's a normal file chooser. One shot at a time. */}
                <label
                  data-testid="moment-take-photo"
                  className="inline-flex items-center gap-1 text-sm text-pink-600 hover:text-pink-700 cursor-pointer"
                >
                  <CameraIcon className="h-4 w-4" />
                  Take photo
                  <input
                    type="file"
                    data-testid="moment-photo-input"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </label>
                {/* Choose from gallery: no `capture`, so mobile opens the photo
                    library and desktop the file picker; multiple allowed. */}
                <label
                  data-testid="moment-choose-photo"
                  className="inline-flex items-center gap-1 text-sm text-pink-600 hover:text-pink-700 cursor-pointer"
                >
                  <PhotoIcon className="h-4 w-4" />
                  Choose photo
                  <input
                    type="file"
                    data-testid="moment-gallery-input"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </label>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {photos.length} / {MAX_PHOTOS} photos · at least one required
            </p>
          </div>

          {/* Caption — optional. */}
          <textarea
            data-testid="moment-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_LIMIT))}
            placeholder={`Say something about ${studentName}'s moment… (optional)`}
            className="block w-full rounded-md border-gray-300 mb-1 h-24 resize-y"
          />
          <p className="text-xs text-gray-400 mb-4 text-right">
            {caption.length} / {CAPTION_LIMIT}
          </p>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="moment-create-cancel-btn"
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="moment-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-3 py-2 text-sm text-white bg-pink-600 rounded-md hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Sharing…' : 'Share moment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MomentCreateModal;
